import { openDB, type IDBPDatabase } from "idb";

// Spec §4.2: "Tokens are stored in IndexedDB alongside the Replicache cache".
// One shared `johndo` database; Replicache will get its own stores in this
// same DB when sync lands. We cache the User row too so the SPA can paint
// instantly on cold load and stay usable offline.

export type Session = {
  access_token: string;
  refresh_token: string;
  // Access-token expiry as ms-since-epoch. Derived once from the JWT `exp`
  // claim so the background refresher doesn't need to re-parse.
  expires_at: number;
};

export type CachedUser = {
  id: string;
  email: string;
  display_name: string | null;
  handle: string | null;
  is_admin?: boolean;
};

const DB_NAME = "johndo";
const STORE = "auth";
const SESSION_KEY = "session";
const USER_KEY = "user";

let dbp: Promise<IDBPDatabase> | null = null;
function db(): Promise<IDBPDatabase> {
  if (!dbp) {
    dbp = openDB(DB_NAME, 1, {
      upgrade(d) {
        if (!d.objectStoreNames.contains(STORE)) d.createObjectStore(STORE);
      },
    });
  }
  return dbp;
}

export async function loadSession(): Promise<Session | null> {
  const d = await db();
  return ((await d.get(STORE, SESSION_KEY)) as Session | undefined) ?? null;
}

export async function saveSession(s: Session): Promise<void> {
  const d = await db();
  await d.put(STORE, s, SESSION_KEY);
}

export async function loadCachedUser(): Promise<CachedUser | null> {
  const d = await db();
  return ((await d.get(STORE, USER_KEY)) as CachedUser | undefined) ?? null;
}

export async function saveCachedUser(u: CachedUser): Promise<void> {
  const d = await db();
  await d.put(STORE, u, USER_KEY);
}

export async function clearAuthStorage(): Promise<void> {
  const d = await db();
  await Promise.all([d.delete(STORE, SESSION_KEY), d.delete(STORE, USER_KEY)]);
}
